import { fetchRequestSend } from "../../helpers/common-helpers";
import l10n from '../../lang/locallang';
import CatalogBreadcrumb from './CatalogBreadcrumb';
import CatalogHeader from './CatalogHeader';

export default class CatalogNavigationClass{
    constructor() {
        // The tile body is a fixed height: past this many names it shows MAX-1 plus an "and N more" line.
        this.tilePreviewMax = 6;

        // Inert while `.course-tile__image img` fixes both in CSS — kept so this and the PHP element
        // describe the same box.
        this.tileCoverWidth = 310;
        this.tileCoverHeight = 102;

        // The cheat-sheet tile's download glyph. Its other copy is
        // `templates/Element/svg/download_arrow.php`, which the PHP tile renders — change both.
        this.downloadIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path
            d="M8 1a.9.9 0 0 1 .9.9v6.3l2-2a.9.9 0 1 1 1.27 1.27l-3.5 3.5a.9.9 0 0 1-1.27 0l-3.5-3.5A.9.9 0 1 1 5.17 6.2l2 2V1.9A.9.9 0 0 1 8 1z
            M2.5 12.2a.9.9 0 0 1 .9.9v.5h9.2v-.5a.9.9 0 1 1 1.8 0v.9a1.4 1.4 0 0 1-1.4 1.4H3.1a1.4 1.4 0 0 1-1.4-1.4v-.9a.9.9 0 0 1 .8-.9z"/></svg>`;
        this.breadcrumb = new CatalogBreadcrumb();
        this.header = new CatalogHeader();
        // There is no endpoint that returns the root topic grid — the server only ever renders it
        // inline — so the root crumb can restore it in place only on a page loaded at the root. On a
        // deep link the crumb stays an ordinary link and loads the page.
        this.rootGridHtml = this.breadcrumb.isAtRoot()
            ? document.querySelector('.js-catalog-grid')?.innerHTML
            : null;
        this.initializeEventListeners();
    }

    initializeEventListeners() {
        this.openNextLevelEvent(); // Sets up listener for opening next level of navigation.
    }

    openNextLevelEvent() {
        window.addEventListener('click', e => {
            if (e.target.closest('.open-topic__js')) {
                e.preventDefault();
                this.navigateTo(e.target.closest('.open-topic__js'));
                return;
            }

            const crumb = e.target.closest('.js-topic-breadcrumb-link');
            if (crumb) {
                e.preventDefault();
                // Climbing runs the drill-down the sidebar twin would have run. A synthetic click on
                // that twin is not an option: `menuStatus` toggles an already-active parent shut.
                const twin = document.querySelector(`.js-catalog-tree .open-topic__js[data-topic="${crumb.dataset.topic}"]`);
                if (twin) {
                    this.navigateTo(twin);
                }
                return;
            }

            if (e.target.closest('.js-catalog-root-link') && this.rootGridHtml) {
                e.preventDefault();
                this.restoreRoot();
            }
        });
    }

    restoreRoot() {
        const grid = document.querySelector('.js-catalog-grid');

        grid.innerHTML = this.rootGridHtml;
        // The root level is nobody's next level, and this path does not go through `renderTiles`.
        grid.dataset.topicId = '';
        this.clearTreeMenuStatus();
        this.clearMyTrainingMenuStatus();
        this.breadcrumb.renderRoot();
        this.header.renderRoot();
        window.history.pushState({}, '', this.breadcrumb.rootUrl);
    }

    clearTreeMenuStatus() {
        document.querySelectorAll('.js-catalog-tree .active').forEach((item) => item.classList.remove('active'));
    }

    navigateTo(element) {
        this.openNextLevel(element);
        this.menuStatus(element);
        this.breadcrumb.renderForTopic(element);
        this.header.renderForTopic(element);
        const searchInput = document.querySelector('.js-catalog-search');
        if (searchInput) {
            searchInput.value = '';
        }

        this.changeURL(element);
        this.clearMyTrainingMenuStatus();
    }

    clearMyTrainingMenuStatus() {
        document.querySelectorAll('.open-course__js').forEach(item => item.classList.remove('active'));
    }

    openNextLevel(element) {
        let wrapper = document.querySelector('.js-catalog-grid');
        let url = '';

        switch (wrapper.id) {
            case 'qr-list':
                url = element.dataset.level === 'topic' ? getQrSubTopics : getSubTopicQrs;
                break;
            case 'catalog-list':
                url = element.dataset.level === 'topic' ? getSubTopics : getSubTopicCourses;
                break;
            default:
                break;
        }

        let body = {
            parent_topic_id: element.dataset.topic,
            sub_topic_id: element.dataset.topic,
        };
        fetchRequestSend(url, 'POST', body).then(data => {
            let list = [];
            let type = '';
            if (data.status) {
                if(data.sub_topics){
                    list = data.sub_topics;
                    type = 'topic';
                } else if(data.courses) {
                    list = data.courses;
                    type = 'course';
                } else if(data.response) {
                    list = data.response;
                    type = 'response';
                }
                this.renderTiles(list, wrapper.id, type, false, element.dataset.topic);
            }
        });
    }

    changeURL(element) {
        let section = 'courses';
        if(document.querySelector('#qr-list')){
            section = 'references';
        }
        let slug = element.dataset.slug;
        if (!slug) {
            return;
        }

        let currentUrl = new URL(window.location.href);
        let urlParts = currentUrl.pathname.split('/').filter(part => part);

        const siteCodeLang = document.documentElement.lang;
        const supportedLangs = ['es'];

        if (supportedLangs.includes(urlParts[0])) {
            urlParts.shift();
        }

        let catalogIndex = urlParts.indexOf(section);
        let isChildSubject = element.dataset.level !== 'topic';

        if (catalogIndex === -1) {
            urlParts.unshift(section);
            catalogIndex = 0;
        }
        if (isChildSubject) {
            if (urlParts.length === catalogIndex + 1) {
                urlParts.push('placeholder-subject');
            }
            urlParts[catalogIndex + 2] = slug; // Add/replace child subject slug
        } else {
            urlParts[catalogIndex + 1] = slug;
            if (urlParts.length > catalogIndex + 2) {
                urlParts.splice(catalogIndex + 2);
            }
        }

        currentUrl.pathname =
            siteCodeLang === 'es'
                ? `/es/${urlParts.join('/')}`
                : `/${urlParts.join('/')}`;

        window.history.pushState({}, '', currentUrl);
    }


    /**
     * `topicId` records WHICH topic the tiles that land here belong to. Read by the topic Settings
     * panel, whose preview tile lists this level: the grid keeps the previous topic's tiles for the
     * whole request, and a search fills it with matches that belong to no topic at all, so a reader
     * cannot tell from the tiles alone. Empty = speaks for no topic.
     */
    renderTiles(data, wrapperId, type, isSearch = false, topicId = '') {
        let wrapper = document.getElementById(wrapperId);
        let tiles = '';

        data.forEach(item => {
            tiles += this.renderTile(item, wrapperId, type, isSearch);
        });
        wrapper.innerHTML = tiles;
        wrapper.dataset.topicId = isSearch ? '' : topicId;
    }

    /**
     * Escapes a tenant-supplied name for a double-quoted HTML attribute — these tiles are built as
     * an HTML string, so a name carrying a quote would otherwise break out of the attribute.
     */
    escapeAttr(value) {
        return String(value ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    }

    renderTile(item, wrapperId, type, isSearch = false) {
        const { quickReference, course } = l10n.catalog;
        let item_type = wrapperId === 'qr-list' ? quickReference : course;
        let topic_image = '';

        const tileImageId = 'tile-image-' + (item.id || Math.random().toString(36).substr(2,9));

        // What the header card reads when this tile is the next drill-down. A JSON cover blob
        // ({type,url}) is never a usable src, so the header falls back to the account art instead.
        const headerCoverRaw = typeof item.cover_image === 'string' ? item.cover_image.trim() : '';
        const headerCover = headerCoverRaw.charAt(0) === '{' ? '' : headerCoverRaw;

        // A courseware item renders as a portrait `.quick-reference-item`, so it must not inherit 310x102.
        const coverSize = item.courseware
            ? ''
            : ` width="${this.tileCoverWidth}" height="${this.tileCoverHeight}"`;

        if (item.cover_image) {
            const rawCover = item.cover_image;
            // cover_image may be a JSON blob ({type,url}) for custom courses — never put that raw in src;
            // getCoverImage() below resolves it and sets the real src.
            const isJsonCover = typeof rawCover === 'string' && rawCover.trim().charAt(0) === '{';
            const coverPath = isJsonCover ? 'images/placeholder-image.png' : this.replaceImagesToThumbnails(rawCover);
            const coverSrc = window.location.origin + webrootPath + coverPath;
            topic_image = `<img id="${tileImageId}" src="${coverSrc}" alt ${coverSize} loading="lazy">`;
        } else if (item.qr_cover_image) {
            const qrSrc = `${window.location.origin + webrootPath}images/cover-images/${item.slug}.webp`;
            topic_image = `<img id="${tileImageId}" src="${qrSrc}" alt ${coverSize} loading="lazy">`;
        } else if (item.courseware) {
            // `alt` is empty because the tile now prints the name beside the art, and an unreachable
            // sheet hides its own image so the frame stays a clean panel.
            topic_image = `<img id="${tileImageId}" src="${this.escapeAttr(item.courseware[0].image_url)}"
                alt="" loading="lazy" onerror="this.style.visibility='hidden'">`;
        } else {
            const fallbackSrc = `${window.location.origin + webrootPath}images/placeholder-image.png`;
            topic_image = `<img id="${tileImageId}" src="${fallbackSrc}" alt ${coverSize} loading="lazy">`;
        }

        this.getCoverImage(item)
            .then(([coverImage, coverType]) => {
                const imgElem = document.getElementById(tileImageId);
                if (!coverImage || !imgElem) {
                    return;
                }
                if (coverType === 'image') {
                    imgElem.src = coverImage;
                    return;
                }
                // A colour or gradient is a background, not a src: the PHP tile paints the box and
                // renders no <img>, so the placeholder standing in for it has to go with it.
                imgElem.parentElement.style.background = coverImage;
                imgElem.classList.add('hidden');
            })
            .catch(error => {
                console.error("Error fetching cover image:", error);
            });

        let url_course = '#';
        let subitems = '';

        const preview = item.modules || item.courses || item.courseware || [];
        // The endpoints ship only the first 6 entries but carry the real figure alongside, so the
        // remainder has to come from the count — the array we were handed is already truncated.
        let total = preview.length;
        if (item.modules && item.count_modules) {
            total = item.count_modules;
        } else if (item.courses && item.course_count) {
            total = item.course_count;
        }
        const shown = total > this.tilePreviewMax
            ? preview.slice(0, this.tilePreviewMax - 1)
            : preview;
        shown.forEach((entry) => {
            subitems += `<li>${this.escapeAttr(entry.name)}</li>`;
        });
        if (total > this.tilePreviewMax) {
            const remainder = total - (this.tilePreviewMax - 1);
            subitems += `<li>${this.escapeAttr(l10n.catalog.andNMore.replace('{0}', remainder))}</li>`;
        }

        let class_js = '';
        switch (type) {
            case 'topic':
            case 'course':
                class_js = 'open-topic__js';
                break;
            default:
                break;
        }
        if (isSearch) {
            class_js = '';
        }
        if (item.courseware) {
            url_course = item.courseware[0].qr_url ? item.courseware[0].qr_url : '#';
        } else {
            const isEs = +item.language_id === 2;
			const isLms = document.body.dataset.context === 'lms';

          	const courseIdentifier = isLms ? item.id : item.slug;
            let template = null;

            // The QR catalog page defines neither template global.
            if (isEs && typeof courseTemplateEs !== 'undefined') {
                template = courseTemplateEs;
            } else if (typeof courseTemplateEn !== 'undefined') {
                template = courseTemplateEn;
            }

			if (template && courseIdentifier) {
			    url_course = template.replace('__SLUG__', courseIdentifier);
			}

        }

        // Twin of course_catalog_tile.php: sub-topics arrive as 'topic', courses as anything else or a search.
        const isCatalogGrid = wrapperId === 'catalog-list';
        const isCourseTile = isCatalogGrid && (type !== 'topic' || isSearch);
        const isGroupHidden = Boolean(item.is_hidden);
        let visibilityHook = '';
        if (isCatalogGrid) {
            visibilityHook = isCourseTile ? ' js-catalog-course' : ' js-catalog-topic';
        }
        const visibilityClasses = `${visibilityHook}${isGroupHidden ? ' is-group-hidden' : ''}`;
        const hiddenCountAttr = item.hidden_count !== undefined ? ` data-hidden-count="${parseInt(item.hidden_count, 10) || 0}"` : '';
        const visibilityAttr = `${isGroupHidden ? ' data-group-hidden="1"' : ''}${hiddenCountAttr}`;
        const hiddenBadge = isGroupHidden
            ? `<span class="course-tile__badge cg-badge cg-badge--inactive">${this.escapeAttr(l10n.catalog.hidden)}</span>`
            : '';

        let tile = `
        <div class="catalog-grid__cell">
            <a href="${url_course}" class="course-tile ${class_js}${visibilityClasses}"${visibilityAttr}
               data-slug="${item.slug}" data-topic="${item.id}" data-level="subtopic"
               data-name="${this.escapeAttr(item.name)}"
               data-cover="${this.escapeAttr(headerCover)}" data-count="${this.escapeAttr(item.course_count || '')}">
                <div class="course-tile__cover">
                    <div class="course-tile__image">
                        ${topic_image}
                    </div>
                    <div class="course-tile__overlay">
                        <h3 class="course-tile__title">${this.escapeAttr(item.name)}</h3>
                        <p class="course-tile__meta">
                            ${item.course_count ? `<span class="js-catalog-tile-count">${item.course_count} ${item.course_count > 1 ? item_type + 's' : item_type}</span>` : ''}
                            ${item.hours && typeof userType === 'undefined' ? `<i class="icon icon-cg-small"></i>${item.hours}` : ''}
                            ${
                            typeof userType !== 'undefined' && item.owner_icon && item.hours
                                ? `<img
                                        src="${item.owner_icon}"
                                        alt
                                        id="accountIcon"
                                        width="18"
                                        height="18"
                                        class="course-tile__owner-icon"
                                    >
                                    ${item.hours}`
                                : ''
                            }
                        </p>
                    </div>
                    ${hiddenBadge}
                </div>
                <div class="course-tile__body">
                    <ul class="course-tile__list">
                        ${subitems}
                    </ul>
                </div>
            </a>
        </div>`;

        if (item.courseware) {
            tile = `
            <div class="catalog-grid__cell">
                <a href="${url_course}" target="_blank" rel="noopener noreferrer" class="quick-reference-item ${class_js}">
                    <div class="quick-reference-item__frame">
                        ${topic_image}
                    </div>
                    <div class="quick-reference-item__footer">
                        <span class="quick-reference-item__name">${this.escapeAttr(item.name)}</span>
                        <span class="quick-reference-item__pdf">
                            <span class="quick-reference-item__pdf-icon" aria-hidden="true">${this.downloadIcon}</span>
                            PDF
                        </span>
                    </div>
                </a>
            </div>
        `;
        }
        return tile;
    }


    menuStatus(button) {
        if (button.closest('.sidebar')) {
            if (button.closest('.__has-submenu')) {
                let buttons = button.closest('.sidebar').querySelectorAll('.open-topic__js');
                buttons.forEach((btn) => {
                    if (btn != button) {
                        if (btn.classList.contains('active')) {
                            btn.classList.remove('active');
                            if (btn.closest('.__has-submenu')) {
                                btn.closest('.__has-submenu').classList.remove('active');
                            }
                        }
                    }
                });
                if (!button.classList.contains('active')) {
                    button.classList.add('active');
                    button.closest('.__has-submenu').classList.add('active');
                } else {
                    button.classList.remove('active');
                    if (button.closest('.__has-submenu').classList.contains('active')) {
                        button.closest('.__has-submenu').classList.remove('active');
                    }
                }
            }
        }
        if (button.closest('#catalog-list') || button.closest('#qr-list')) {
            let topic_id = button.getAttribute('data-topic');
            let sidebar_button = document.querySelector(`.sidebar .open-topic__js[data-topic="${topic_id}"]`);
            if (sidebar_button) {
                let buttons = document.querySelector('.sidebar').querySelectorAll('.open-topic__js');
                buttons.forEach((btn) => {
                    if (btn != sidebar_button) {
                        if (btn.classList.contains('active')) {
                            btn.classList.remove('active');
                            btn.closest('.__has-submenu').classList.remove('active');
                        }
                    }
                });
                sidebar_button.classList.add('active');
                if (sidebar_button.closest('.__has-submenu')) {
                    sidebar_button.closest('.__has-submenu').classList.add('active');
                }
            }
        }
    }

    replaceImagesToThumbnails(image) {
        if(!image){
            return false;
        }

        if(typeof(image) === 'object') {
            return false;
        }
        let originalPath = image;

        let pathInfo = originalPath.split('/');
        let directory = pathInfo.slice(0, -1).join('/');
        let filenameWithExtension = pathInfo[pathInfo.length - 1].split('.');
        let filename = filenameWithExtension.slice(0, -1).join('');
        let extension = filenameWithExtension[filenameWithExtension.length - 1];

        let newFilename = filename + '_thumb.' + extension;
        let thumbnailPath = directory + '/thumbnails/' + newFilename;

        return thumbnailPath
    }

    /** @returns {Promise<Array>} `[coverImage, coverType]` — coverType is image, color or gradient. */
    async getCoverImage(course) {
        const webroot = window.location.origin + '/';
        let coverImage = null;
        let coverType = 'image';

        if (course.cover_image) {
            let cover = course.cover_image;
            try {
                const parsed = JSON.parse(cover);

                if (parsed && parsed.type) {
                    switch (parsed.type) {
                        case 'image':
                            coverImage = parsed.url;
                            break;
                        case 'color':
                            coverImage = parsed.color;
                            coverType = 'color';
                            break;
                        case 'gradient':
                            coverImage = 'linear-gradient(' + parsed.gradient.join(',') + ')';
                            coverType = 'gradient';
                            break;
                        default:
                            coverImage = webroot + cover;
                            break;
                    }
                } else {
                    coverImage = webroot + cover;
                }
            } catch (e) {
                coverImage = webroot + cover;
            }
        } else if (course.subjects_new && course.subjects_new.cover_image) {
            coverImage = webroot + course.subjects_new.cover_image;
        }

        // A leading "<digits>/" means an S3 path, which resolves only through CloudFront.
        if (coverType === 'image' && coverImage && /^\d+\//.test(coverImage)) {
            const cloudFrontUrl = await this.getCloudFrontContent(coverImage);
            if (cloudFrontUrl) {
                coverImage = cloudFrontUrl;
            }
        }

        return [coverImage, coverType];
    }

    /** @returns {Promise<string|boolean>} The resolved URL, or false when the lookup fails. */
    async getCloudFrontContent(imageUrl) {
        const data = {
            path: imageUrl,
            isCustomContent: true
        };

        try {
            const response = await fetch('/aws/get-cloud-front-content', {
                method: 'POST',
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const jsonResponse = await response.json();
            return jsonResponse.url;
        } catch (error) {
            console.error('Failed to fetch URL:', error);
            return false;
        }
    }

}
